<template>
  <div class="buyer-campaign-details-wrapper">
    <!-- Floating Navigation Menu -->
    <nav class="section-nav">
      <ul class="section-nav__list">
        <li
          v-for="item in navigationItems"
          :key="item.id"
          class="section-nav__item"
        >
          <a
            :href="`#${item.id}`"
            :class="['section-nav__link', item.linkClass]"
            @click.prevent="navigateToSection(item.id)"
          >
            <CIcon
              v-if="item.icon"
              :name="item.icon"
              size="sm"
              class="mr-1"
            />
            {{ item.label }}
          </a>
        </li>
      </ul>
    </nav>

    <div class="buyer-campaign-details-content">
      <CModal
        :title="`Confirm ${action}`"
        :color="modalHeaderColor"
        data-cy="approve-reject-modal"
        :show.sync="warningActionModal"
      >
        Please confirm that you want to {{ action }} this campaign?

        <CAlert
          v-if="action === 'approve'"
          color="warning"
          class="mt-2 mb-0 p-2 pr-1 d-flex align-items-center"
        >
          <CIcon
            name="cil-warning"
            class="mr-3 flex-shrink-0"
          />
          Campaign will be paused upon approval. Coordinate with buyer when they want to start receiving calls.
        </CAlert>

        <CTextarea
          v-model="remarks"
          class="mt-2"
          name="Remarks"
          label="Remarks"
          :is-valid="areRemarksValid"
          invalid-feedback="Remarks are mandatory for rejection"
          small
          required
          rows="3"
          :debounce="500"
        />

        <template #footer>
          <ButtonLoading
            v-if="action === 'reject'"
            color="primary"
            :loading="isApproveRejectButtonLoading"
            :disabled="!areRemarksValid"
            @click="confirmRejectCampaign"
          >
            Reject
          </ButtonLoading>
          <ButtonLoading
            v-if="action === 'approve'"
            color="primary"
            :loading="isApproveRejectButtonLoading"
            @click="confirmApproveCampaign"
          >
            Approve
          </ButtonLoading>

          <CButton
            color="danger"
            @click="warningActionModal = false"
          >
            Cancel
          </CButton>
        </template>
      </CModal>

      <CCard
        v-if="buyerAccountDetails && buyerAccountDetails.id"
        id="section-buyer-account"
      >
        <CCardHeader>
          Buyer Account
        </CCardHeader>
        <CCardBody>
          <dl class="row">
            <dt class="col-sm-3 text-right">
              Account Name
            </dt>
            <dd class="col-sm-9 d-flex align-items-center">
              <router-link
                :to="{ name: 'BuyerAccountDetails', params: { id: buyerAccountDetails.id} }"
                target="_blank"
              >
                {{ buyerAccountDetails.name }}
              </router-link>

              <a
                href
                class="ml-1"
                @click.prevent="
                  copyAccountIdToClipboard(buyerAccountDetails.id)
                "
              ><CIcon
                name="cil-copy"
              /></a>
            </dd>
          </dl>
        </CCardBody>
      </CCard>

      <MainSection
        v-if="
          campaignDetails &&
            campaignDetails.id &&
            buyerAccountDetails &&
            buyerAccountDetails.id
        "
        id="section-main"
        :campaign-details="campaignDetails"
        :is-cpa-category="isCpaCategory"
        @pause-resume-completed="onCampaignChanged"
        @details-updated="onCampaignChanged"
      />
      <Destination
        v-if="campaignDetails && campaignDetails.id"
        id="section-destination"
        :campaign-id="campaignDetails.id"
        :routing-details="campaignDetails.routing"
        :budget-details="campaignDetails.budget"
        :is-cpa-category="isCpaCategory"
        @routing-type-changed="pendingRoutingType = $event"
        @details-updated="onCampaignChanged"
      />
      <Scheduling
        v-if="campaignDetails && campaignDetails.id"
        id="section-scheduling"
        :campaign-id="campaignDetails.id"
        :routing-details="campaignDetails.routing"
        :pending-routing-type="pendingRoutingType"
        :account-time-zone-id="getSafe(() => buyerAccountDetails.timeZoneId)"
        @details-updated="onCampaignChanged"
      />
      <TrafficSources
        v-if="
          campaignDetails &&
            campaignDetails.id &&
            buyerAccountDetails &&
            buyerAccountDetails.id
        "
        id="section-traffic-sources"
        :campaign-id="campaignDetails.id"
        :traffic-sources="buyerAccountDetails.allowedTrafficSources"
        :traffic-sources-details="campaignDetails.trafficSources"
        :buyer-restrict-duplicates="buyerAccountDetails.campaignSettings?.restrictDuplicates"
        @details-updated="onCampaignChanged"
      />
      <Audience
        v-if="campaignDetails && campaignDetails.id"
        id="section-audience"
        :campaign-id="campaignDetails.id"
        :account-id="getSafe(() => buyerAccountDetails.id)"
        :audience-details="campaignDetails.audience"
        :zip-codes-file-link="campaignDetails.zipCodesFileLink"
        @details-updated="onCampaignChanged"
      />
      <Budget
        v-if="campaignDetails && campaignDetails.id"
        id="section-budget"
        :campaign-id="campaignDetails.id"
        :campaign-routing-type="campaignDetails.routing.type"
        :budget-details="campaignDetails.budget"
        :is-cpa-category="isCpaCategory"
        @details-updated="onCampaignChanged"
      />

      <CCard
        v-if="campaignDetails && campaignDetails.id"
        id="section-seller-control"
      >
        <CCardHeader class="d-flex align-items-center">
          <CIcon
            name="cil-transfer"
            class="mr-2"
          />
          <strong>Campaign Seller Control</strong>
        </CCardHeader>

        <CCardBody>
          <BuyerCampaignTrafficControl
            :campaign-id="campaignDetails.id"
            :category-id="campaignDetails.category.id"
            :buyer-account-id="buyerAccountDetails.id"
            @details-updated="onCampaignChanged"
          />
        </CCardBody>
      </CCard>

      <ReviewNotes
        v-if="buyerAccountDetails && campaignDetails && campaignDetails.id"
        id="section-notes"
        ref="reviewNotes"
        :parent-object="campaignDetails"
      />

      <CRow
        v-if="isInReview"
        class="mb-3"
      >
        <CCol sm="1">
          <ButtonLoading
            color="success"
            @click="approveCampaign"
          >
            Approve
          </ButtonLoading>
        </CCol>

        <CCol sm="1">
          <ButtonLoading
            color="danger"
            class="ml-2"
            @click="rejectCampaign"
          >
            Reject
          </ButtonLoading>
        </CCol>
      </CRow>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapState } from 'vuex'

import MainSection from './MainSection.vue'
import Destination from './Destination.vue'
import Scheduling from './Scheduling.vue'
import TrafficSources from './TrafficSources.vue'
import Audience from './Audience.vue'
import Budget from './Budget.vue'
import BuyerCampaignTrafficControl from './BuyerCampaignTrafficControl.vue'
import ReviewNotes from '../../ReviewNotes/ReviewNotes.vue'

import { showSuccessMessage } from '@/notification-utils'
import copyToClipboardMixin from '@/mixins/copy-to-clipboard-mixin'
import hashNavigationMixin from '@/mixins/hash-navigation-mixin'

export default {
  name: 'BuyerCampaignDetails',

  title: 'Buyer Campaign',

  components: {
    MainSection,
    Destination,
    Scheduling,
    TrafficSources,
    Audience,
    Budget,
    BuyerCampaignTrafficControl,
    ReviewNotes
  },

  mixins: [copyToClipboardMixin, hashNavigationMixin],
  
  data() {
    return {
      isApproveRejectButtonLoading: false,
      warningActionModal: false,
      action: '',
      remarks: '',
      pendingRoutingType: null
    }
  },
  computed: {
    ...mapGetters('campaign', ['buyerCampaignById']),
    ...mapState('accounts', ['buyerAccountDetails']),
    ...mapState('category', ['categories']),

    campaignDetails() {
      return this.buyerCampaignById(this.campaignId)
    },

    isCpaCategory() {
      const categoryId = this.campaignDetails?.category?.id
      const category = this.categories.find(c => c.id === categoryId)
      return category?.isCpaConversionOnly ?? false
    },
    modalHeaderColor() {
      const colorMap = { reject: 'danger', approve: 'success' }
      return colorMap[this.action]
    },
    areRemarksValid() {
      if (this.action === 'approve') {
        return true
      }

      if (this.action === 'reject' && this.remarks.length) {
        return true
      }

      return false
    },

    isInReview() {
      return this.campaignDetails?.status?.toLowerCase() === 'inreview'
    },

    navigationItems() {
      return [
        { id: 'section-main', label: 'Campaign Details', icon: 'cil-info' },
        { id: 'section-destination', label: 'Destination', icon: 'cil-phone' },
        ...this.programmaticNavigationItems,
        { id: 'section-scheduling', label: 'Scheduling', icon: 'cil-calendar' },
        { id: 'section-traffic-sources', label: 'Traffic Sources', icon: 'cil-transfer' },
        { id: 'section-audience', label: 'Audience', icon: 'cil-people' },
        { id: 'section-budget', label: 'Budget', icon: 'cil-wallet' },
        { id: 'section-seller-control', label: 'Seller Control', icon: 'cil-settings' },
        { id: 'section-notes', label: 'Notes', icon: 'cil-notes' }
      ]
    },

    programmaticNavigationItems() {
      const routing = this.campaignDetails?.routing

      if (routing?.type !== 'Programmatic') {
        return []
      }

      const target = routing.programmaticTarget || {}
      const bidSettings = target.bidSettings || {}
      const bidAcceptanceSettings = target.bidAcceptanceSettings || {}
      const linkClass = 'section-nav__link--sub'

      const items = [
        { id: 'section-bid-settings', label: 'Bid Settings', linkClass },
        { id: 'section-request-settings', label: 'Request Settings', linkClass }
      ]

      if (target.isNumberDynamic) {
        items.push({ id: 'section-dynamic-number-parsing', label: 'Dynamic Number Parsing', linkClass })
      }

      if (bidSettings.isBidDynamic) {
        items.push({ id: 'section-bid-parsing', label: 'Bid Parsing', linkClass })
      }

      items.push({ id: 'section-call-acceptance-parsing', label: 'Call Acceptance', linkClass })

      if (bidSettings.conversionCallLengthDynamic && !this.isCpaCategory) {
        items.push({ id: 'section-dynamic-duration-parsing', label: 'Dynamic Duration Parsing', linkClass })
      }

      items.push({ id: 'section-confirmation-request-settings', label: 'Confirmation Request', linkClass })

      if (bidAcceptanceSettings.enabled && bidAcceptanceSettings.bidIdRequired) {
        items.push({ id: 'section-bid-id-parsing', label: 'Bid ID', linkClass })
      }

      if (bidAcceptanceSettings.enabled && bidAcceptanceSettings.numberRequired) {
        items.push({ id: 'section-number-sip-parsing', label: 'Number / SIP', linkClass })
      }

      return items
    }
  },

  async created() {
    this.campaignId = this.$route.params.id

    await this.fetchBuyerCampaign(this.campaignId)

    document.title = this.campaignDetails.name

    const { accountId } = this.campaignDetails

    await this.getBuyerAccountDetails(accountId)

    this.fetchCampaignSpend().catch(() => {})

    // Trigger hash navigation after data is loaded so sections are rendered
    this.$nextTick(() => {
      this.handleHashNavigation()
    })
  },

  methods: {
    ...mapActions('campaign', [
      'fetchBuyerCampaigns',
      'approveBuyerCampaign',
      'rejectBuyerCampaign',
      'fetchBuyerCampaign'
    ]),

    ...mapActions('accounts', ['getBuyerAccountDetails']),
    ...mapActions('insights', ['fetchCampaignSpend']),

    async onCampaignChanged() {
      await this.fetchBuyerCampaign(this.campaignId)

      // any card can trigger a refetch, so only drop the pending routing type
      // once the saved campaign actually has it - otherwise the Scheduling card
      // would resync from a campaign whose routing switch is still unsaved
      if (this.campaignDetails?.routing?.type === this.pendingRoutingType) {
        this.pendingRoutingType = null
      }

      this.$refs.reviewNotes?.fetchNotes()
    },

    approveCampaign() {
      this.action = 'approve'
      this.warningActionModal = true
    },

    rejectCampaign() {
      this.action = 'reject'
      this.warningActionModal = true
    },

    async confirmApproveCampaign() {
      try {
        this.isApproveRejectButtonLoading = true
        await this.approveBuyerCampaign({
          id: this.campaignId,
          remarks: this.remarks,
          version: this.campaignDetails.version
        })
        this.warningActionModal = false
        this.isApproveRejectButtonLoading = false
        this.$router.push({
          name: 'PendingApprovalBuyerCampaigns'
        })
      } catch (error) {
        // As of now, the interceptor shows the error popup but
        // if we want to do some custom handling in future we
        // can do it here
      } finally {
        this.isApproveRejectButtonLoading = false
      }
    },

    async confirmRejectCampaign() {
      try {
        this.isApproveRejectButtonLoading = true
        await this.rejectBuyerCampaign({
          id: this.campaignId,
          remarks: this.remarks,
          version: this.campaignDetails.version
        })
        this.isApproveRejectButtonLoading = false
        this.warningActionModal = false
        this.$router.push({
          name: 'PendingApprovalBuyerCampaigns'
        })
      } catch(error) {
        // As of now, the interceptor shows the error popup but
        // if we want to do some custom handling in future we
        // can do it here
      } finally {
        this.isApproveRejectButtonLoading = false
      }
    },

    copyAccountIdToClipboard(id) {
      this.copyTextToClipboard(id)
      showSuccessMessage('Account ID copied to clipboard')
    }
  }
}
</script>

<style lang="scss" scoped>
.buyer-campaign-details-wrapper {
  position: relative;
  margin-left: -0.75rem;
  margin-right: -0.75rem;
}

.buyer-campaign-details-content {
  margin-right: 145px;

  @media (max-width: 1200px) {
    margin-right: 0;
  }
}

.section-nav {
  position: fixed;
  top: 120px;
  right: 18px;
  width: 125px;
  max-height: calc(100vh - 140px);
  overflow-y: auto;
  background: #fff;
  border: 1px solid #d8dbe0;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  z-index: 100;

  @media (max-width: 1200px) {
    display: none;
  }

  &__list {
    list-style: none;
    margin: 0;
    padding: 4px 0;
  }

  &__item {
    margin: 0;
  }

  &__link {
    display: flex;
    align-items: center;
    padding: 4px 8px;
    color: #4f5d73;
    font-size: 11px;
    text-decoration: none;
    white-space: nowrap;
    transition: background-color 0.15s, color 0.15s;

    &:hover {
      background-color: #ebedef;
      color: #321fdb;
      text-decoration: none;
    }

    &--sub {
      padding-left: 18px;
      font-size: 10px;
    }
  }
}
</style>
